import { isFailedState, isTerminal, type SignalFacet, type TaskState } from '@bloo-team/api-client'
import type { Signal } from './signal-model'

/**
 * A signal's evidence for the docked pane's Overview and Raw events tabs
 * (docs/signals.md §The signal pane): ONE background task over the signal's
 * raw events, the legacy drawer's flow —
 *
 *   live  signals/facets/task_id → poll dispatcher task state → signals/facets/result
 *         (fields), dispatcher task/result pages (events); wrk/api/job/facetrevoke
 *         when the pane goes away while the task still runs.
 *   demo  the demo search job over the signal's raw_event_query → the same poll →
 *         fields counted here from its rows (facetsFromRows), events from its pages.
 *
 * The facet shapes are read from the legacy drawer's code, never captured
 * (packages/api-client/src/signals.ts).
 */

export interface SignalEvidence {
  /** The task whose result pages are the Raw events tab; null when there is no raw-event query. */
  taskId: string | null
  /** The signal has no raw-event query, so there is nothing to break down. */
  noQuery: boolean
  facets: SignalFacet[]
}

export interface EvidenceScope {
  clusterId: string
  scopeId: string
}

/** The calls the fetch needs — the live and demo paths hand in their own. */
export interface EvidenceDeps {
  start: () => Promise<string | null>
  state: (taskId: string) => Promise<TaskState>
  /** Live: facets/result. Demo: absent — fields are counted from the event rows. */
  facets?: (taskId: string) => Promise<SignalFacet[]>
  rows?: (taskId: string) => Promise<Record<string, unknown>[]>
  revoke: (taskId: string) => Promise<void>
}

/** Polls stop after this long; the task is revoked and the tab says so. */
export const EVIDENCE_TIMEOUT_MS = 120_000

export class EvidenceError extends Error {}

/** One poll interval; an abort ends it early. Each wait removes its own listener, so a long poll does not pile them up on the signal. */
function wait(ms: number, abort?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const onAbort = () => {
      clearTimeout(t)
      resolve()
    }
    const t = setTimeout(() => {
      abort?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    abort?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Start → poll → read. The legacy drawer polled every 200 ms; this backs off
 * to 1 s after the first two seconds. An abort (the pane closed, the signal
 * changed) or the timeout revokes a task that is still running.
 */
export async function fetchEvidence(deps: EvidenceDeps, abort?: AbortSignal, now: () => number = Date.now): Promise<SignalEvidence> {
  const taskId = await deps.start()
  if (taskId === null) return { taskId: null, noQuery: true, facets: [] }
  const started = now()
  let done = false
  try {
    for (let i = 0; ; i++) {
      if (abort?.aborted) throw new EvidenceError('Cancelled')
      const s = await deps.state(taskId)
      if (isFailedState(s.task_state)) throw new EvidenceError(s.task_state === 'REVOKED' ? 'The raw-event task was stopped' : 'The raw-event task failed')
      if (isTerminal(s.task_state)) break
      if (now() - started > EVIDENCE_TIMEOUT_MS) throw new EvidenceError('The raw-event task took too long and was stopped')
      await wait(i < 10 ? 200 : 1000, abort)
    }
    done = true
    const facets = deps.facets ? await deps.facets(taskId) : facetsFromRows(deps.rows ? await deps.rows(taskId) : [])
    return { taskId, noQuery: false, facets }
  } finally {
    if (!done) void deps.revoke(taskId).catch(() => {})
  }
}

/** Fields that describe the event envelope, not the event — left out of the breakdown. */
const ENVELOPE = /^\$?(_|cnamtime$|cnam_time$|scope$|scope_id$|timestamp$)/i

/**
 * The field breakdown counted from event rows (demo): per field, its distinct
 * values with event counts, most frequent first. Fields keep the order they
 * first appear in; empty values are skipped.
 */
export function facetsFromRows(rows: readonly Record<string, unknown>[]): SignalFacet[] {
  const byField = new Map<string, Map<string, number>>()
  for (const row of rows) {
    for (const [k, v] of Object.entries(row)) {
      if (ENVELOPE.test(k) || v === null || v === undefined) continue
      const value = typeof v === 'object' ? JSON.stringify(v) : String(v)
      if (!value.trim()) continue
      const field = k.replace(/^\$/, '')
      let counts = byField.get(field)
      if (!counts) byField.set(field, (counts = new Map()))
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  return [...byField].map(([field, counts]) => {
    const values = [...counts].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value))
    return { field, distinct: values.length, values }
  })
}

/**
 * The demo knowledge base: no KB is authored for the demo detections, so this
 * states only what the signal itself carries and says so.
 */
export function demoSignalKb(s: Pick<Signal, 'detectionName' | 'tactic' | 'technique' | 'subtechnique' | 'streams' | 'rawEventQuery'>): string {
  const attack = [s.tactic, s.technique, s.subtechnique].filter(Boolean).join(' › ')
  return [
    `## ${s.detectionName || 'Detection'}`,
    '',
    attack ? `**MITRE ATT&CK:** ${attack}` : '',
    '',
    s.streams.length ? `**Source streams:** ${s.streams.join(', ')}` : '',
    '',
    s.rawEventQuery ? `**Raw-event query:** \`${s.rawEventQuery}\`` : '',
    '',
    '*Demo data: no knowledge base is written for this detection.*',
  ]
    .filter((l, i, a) => l !== '' || (i > 0 && a[i - 1] !== ''))
    .join('\n')
}
